/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/ui/utils/tailwind";
import Select from "./Select";
import type { SelectOption, FormSelectProps } from "./Select.types";
import InfoIconButton from "@/shared/ui/components/basic/InfoIconButton";
import {
  ALL_VALUE,
  reconcileAllExclusivity,
  withAllOption,
} from "./MultiSelect.helpers";

export { ALL_VALUE } from "./MultiSelect.helpers";

export type MultiSelectProps = Omit<
  FormSelectProps,
  "multiple" | "value" | "onChange" | "data"
> & {
  label?: string;
  data: (SelectOption | string)[];
  value?: string[];
  onChange?: (values: string[]) => void;
  allLabel?: string;
  infoText?: string;
  onInfoClick?: () => void;
};

/**
 * Default no-op change handler for the MultiSelect when none is supplied.
 */
const noopChange: (values: string[]) => void = () => {};

/**
 * Canonical multi-select built on the canonical Select. Prepends an "All"
 * sentinel option that is mutually exclusive with individual selections.
 * When `allLabel` is omitted, uses `common:filters.all` (Todos / All / Todos).
 *
 * @param {MultiSelectProps} props - MultiSelect properties.
 * @returns {JSX.Element} Rendered multi-select field.
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  data,
  value = [ALL_VALUE],
  onChange = noopChange,
  allLabel,
  infoText,
  onInfoClick,
  className,
  ...rest
}) => {
  const { t } = useTranslation("common");
  const options = withAllOption(allLabel ?? t("filters.all"), data);

  const handleChange = useCallback(
    (next: string | string[] | null) => {
      const nextValues = next == null ? [] : Array.isArray(next) ? next : [next];
      const reconciled = reconcileAllExclusivity(value, nextValues);
      onChange(reconciled);
    },
    [value, onChange],
  );

  const showInfo = Boolean(infoText) || Boolean(onInfoClick);

  return (
    <div className={cn("flex flex-col gap-(--space-1)", className)}>
      {(label || showInfo) && (
        <div className="flex items-center gap-(--space-1)">
          {label && (
            <span className="text-body-sm leading-tight text-(--text-secondary)">
              {label}
            </span>
          )}
          {showInfo && (
            <InfoIconButton onClick={onInfoClick} title={infoText} />
          )}
        </div>
      )}
      <Select
        {...rest}
        multiple
        data={options}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};

export { MultiSelect };
export default MultiSelect;
